import { debounce } from '@utils/debounce';

const useFaqAreaBlock = (blockElement: HTMLElement): void => {
  const faqCategories = Array.from(blockElement.querySelectorAll<HTMLLIElement>('.faq-category-block'));
  const filterButtons = blockElement.querySelectorAll<HTMLButtonElement>('.faq-area-block__filter button');
  const areaHeaderButton = blockElement.querySelector<HTMLElement>(".faq-area-block__toggle-button");
  const areaContent = blockElement.querySelector<HTMLElement>(".faq-area-block__content");
  const defaultTimeoutMs = 300;
  const isInFaqList = !!blockElement.closest('.faq-list-block');
  const blockContainer = blockElement.querySelector<HTMLElement>('.faq-area-block__container');
  const isCollapsedByDefault = blockContainer?.dataset.collapsedByDefault === 'true';
  const shouldStartCollapsed = isInFaqList || isCollapsedByDefault;

  const showSelectedCategories = (selectedCategoryIds: number[]) => {
    const selectedCategories = faqCategories.filter((faqCategory) =>
      faqCategory.hasAttribute('data-category-id'));
    const faqCategoriesToShow: HTMLElement[] = [];

    selectedCategories.forEach((faqCategory) => {
      const pageCategoryId: number = JSON.parse(faqCategory.dataset.categoryId!);

      // Show faqCategories that match the selected faqCategories
      if (selectedCategoryIds.includes(pageCategoryId)) {
        faqCategoriesToShow.push(faqCategory);
      }

      // Hide all selected faqCategories initially
      faqCategory.hidden = true;

      // Show as many faqCategories as pagesToShow
      faqCategoriesToShow.forEach((page) => {
        page.hidden = false;
      });
    });
  };

  const handleFilterEvent = (event: MouseEvent): void => {
    const filterButton = event.target as HTMLButtonElement;
    const selectedCategoryIds: number[] = [];

    filterButton.classList.toggle('button--active');
    const isPressed = filterButton.classList.contains('button--active');
    filterButton.setAttribute('aria-pressed', isPressed ? 'true' : 'false');

    // Store selected category id:s
    const selectedFilterButtons = blockElement.querySelectorAll('.button--filter.button--active') as NodeListOf<HTMLButtonElement>;

    selectedFilterButtons.forEach((button) => {
      selectedCategoryIds.push(parseInt(button.parentElement!.dataset.categoryId!));
    });

    // Show faqCategories based on selected category id:s
    if (selectedCategoryIds.length > 0) {
      showSelectedCategories(selectedCategoryIds);
    } else {
      // If no filter is selected, show all faqCategories
      faqCategories.forEach((faqCategory) => {
        faqCategory.hidden = false;
      });
    }
  };

  // Toggle all tabable elements inside the block
  const toggleTabableElements = (blockElement: HTMLElement, isEnabled: boolean) => {
    const tabableElements = blockElement.querySelectorAll<HTMLElement>(
      'a, button:not(.faq-area-block__toggle-button), input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    );

    tabableElements.forEach((el) => {
      if (isEnabled) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', '-1');
      }
    });
  };

  const initializeFaqAreaAccordionFunctionality = () => {
    if (!areaHeaderButton || !areaContent) {
      return;
    }

    if (shouldStartCollapsed) {
      // Start collapsed
      (areaContent as HTMLElement).style.height = "0px";
      blockElement.classList.remove("faq-area-block--is-open");
      areaHeaderButton.setAttribute("aria-expanded", 'false');
      toggleTabableElements(blockElement, false);
    } else {
      // Start expanded
      (areaContent as HTMLElement).style.height = areaContent.scrollHeight + "px";
      blockElement.classList.add("faq-area-block--is-open");
      areaHeaderButton.setAttribute("aria-expanded", 'true');
      toggleTabableElements(blockElement, true);
    }

    areaHeaderButton.addEventListener("click", () => {
      const isOpen = blockElement.classList.contains('faq-area-block--is-open');

      toggleTabableElements(blockElement, !isOpen);

      if (isOpen) {
        // Close
        (areaContent as HTMLElement).style.height = "0px";
        blockElement.classList.remove("faq-area-block--is-open");
        areaHeaderButton.setAttribute("aria-expanded", 'false');
      } else {
        // Open: set to scrollHeight for smooth slide
        (areaContent as HTMLElement).style.height = areaContent.scrollHeight + "px";
        blockElement.classList.add("faq-area-block--is-open");
        areaHeaderButton.setAttribute("aria-expanded", 'true');
      }
    });
  };

  const initializeFilterFunctionality = () => {
    if (filterButtons.length > 0) {
      filterButtons.forEach((button) => {
        button.addEventListener('click', handleFilterEvent);
      });
    }
  };
  
  // If click on any filter or accordion reset height to allow for dynamic height changes
  blockElement.querySelectorAll(".faq-area-block__filter button, .accordion-block__title").forEach((el) => {
    el.addEventListener("click", () => {
      if (blockElement.classList.contains("faq-area-block--is-open") && areaContent) {
        // Reset height to auto to allow for content changes
        areaContent.style.height = "auto";

        // After a short delay, set height back to scrollHeight to enable smooth transition
        setTimeout(() => {
          const newHeight = areaContent.scrollHeight;
          areaContent.style.height = newHeight + "px";
        }, defaultTimeoutMs);
      }
    });
  });

  /* Initializing and add event listener */
  initializeFilterFunctionality();
  initializeFaqAreaAccordionFunctionality();
};

const faqAreaBlockElements = document.querySelectorAll('.faq-area-block') as NodeListOf<HTMLElement>;

if (faqAreaBlockElements.length > 0) {
  faqAreaBlockElements.forEach((blockElement) => {
    useFaqAreaBlock(blockElement);
  });
}

// Handle resize so open blocks keep correct height
const handleResize = debounce(() => {
  const openBlocks = document.querySelectorAll<HTMLElement>(".faq-area-block.faq-area-block--is-open .faq-area-block__content");

  openBlocks.forEach((content) => {
    (content as HTMLElement).style.height = content.scrollHeight + "px";
  });
}, 150);

window.addEventListener("resize", handleResize);
